<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="エクストラネット接続申請"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <validation-observer v-slot="{ handleSubmit }" as="div">
              <v-form @submit.stop.prevent="handleSubmit(submit)">
                <!-- 接続元V番号 -->
                <v-row>
                  <v-col class="detail-container mt-2">
                    <app-label-group
                      label="接続元V番号"
                      :value="contractSummary.vnumber"
                    />
                  </v-col>
                </v-row>

                <!-- 接続元VNコード -->
                <v-row>
                  <v-col cols="12">
                    <app-badge
                      item="接続元VNコード"
                      :is-info="false"
                    ></app-badge>
                    <validation-provider
                      label="接続元VNコード"
                      name="vpnVnCode"
                      :rules="{ required: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                      v-model="form.srcVnCode"
                    >
                      <app-select-search
                        :items="vpnVnCodeList"
                        :item-title="
                          (item: VpnVnCode) =>
                            `${item.vnName}:${item.vpnVnCode}(${item.vnType})`
                        "
                        item-value="vpnVnCode"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      >
                      </app-select-search>
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 接続先V番号 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続先V番号"
                      msg="半角大文字V+半角数字9桁"
                    ></app-badge>
                    <validation-provider
                      v-model="form.dstVNumber"
                      label="接続先V番号"
                      name="dstVNumber"
                      :rules="{ required: true, is_vnumber: true }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 接続先VNコード -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="接続先VNコード"
                      msg="接続元とは異なる先頭Dから始まる半角
                        英字2文字+半角数字4桁"
                    ></app-badge>
                    <validation-provider
                      v-model="form.dstVnCode"
                      label="接続先VNコード"
                      name="dstVnCode"
                      :rules="{
                        required: true,
                        is_vncode: true,
                        is_not_eq: {
                          target: '@vpnVnCode',
                          targetField: '接続元VNコード',
                        },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- PINコード -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="PINコード"
                      :msg="$filter.message('info_pin_code')"
                      :is-required="isRequiredPinCode"
                    ></app-badge>
                    <validation-provider
                      v-model="form.pinCode"
                      label="PINコード"
                      name="pinCode"
                      :rules="{
                        required: isRequiredPinCode,
                        length: { length: 4 },
                        is_half_width_alpha_lowercase_numeric: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- メッセージ -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="メッセージ"
                      msg="記号(_ . , : ; ? < > + - * / = # % & $ @)を
                      含む30文字以内"
                      :isRequired="false"
                    ></app-badge>
                    <validation-provider
                      v-model="form.message"
                      label="メッセージ"
                      name="message"
                      :rules="{
                        max: { length: 30 },
                        is_alphabetic_characters_symbols: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-card-actions>
                  <v-list-item class="w-100 pa-1">
                    <template v-slot:append>
                      <v-btn type="submit" variant="flat" color="primary"
                        >申請</v-btn
                      >
                      <v-btn @click="cancel" variant="outlined" color="primary"
                        >キャンセル</v-btn
                      >
                    </template>
                  </v-list-item>
                </v-card-actions>
              </v-form>
            </validation-observer>
          </v-container>
        </div>
      </v-card-text>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { PostExtranetInfo } from "@/apis/ExtranetApi";
import { mapState } from "vuex";
import type { VpnVnCode } from "@/apis/InformationApi";
import { VueFinalModal } from "vue-final-modal";

export default defineComponent({
  name: "ExtranetConnectRegister",
  props: {
    extranetKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      isLoaded: false,
      form: {} as PostExtranetInfo,
      vpnVnCodeList: [] as VpnVnCode[],
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    ...mapState("user", {
      contractSummary: "contractSummary",
    }),
    /**
     * PINコードの関連項目チェック
     * false: 接続元V番号と接続先V番号同じ, true: 同じではない
     */
    isRequiredPinCode(): boolean {
      return this.contractSummary.vnumber !== this.form.dstVNumber;
    },
  },
  methods: {
    /** データ取得 */
    async load() {
      // VPN/VNコードリストをAPIから取得する
      this.vpnVnCodeList = await this.$api.information.getVpnVnCodeList();
      // L3VNコードのみを対象とする
      this.vpnVnCodeList = this.vpnVnCodeList.filter((v) =>
        v.vpnVnCode.startsWith("D")
      );
      // 主キー相当の項目が無いため、こちらでインデックスを作成
      this.vpnVnCodeList.numbering();
      // VNコードの降順でソート
      this.vpnVnCodeList = this.vpnVnCodeList.sortBy(["vpnVnCode", "desc"]);
    },
    /** エクストラネット接続申請処理 */
    async submit() {
      // 確認ダイアログの表示
      await this.$confirm(`エクストラネット接続を申請します。よろしいですか？
          申請先にて承認された場合、申請されたネットワークと接続されます。
          誤った申請をされた場合、情報漏洩やお客様の通信に支障を及ぼす可能性があります。`);

      // リクエストデータの設定
      const reqData: PostExtranetInfo = {
        srcVnCode: this.form.srcVnCode,
        dstVNumber: this.form.dstVNumber,
        dstVnCode: this.form.dstVnCode,
        pinCode: this.form.pinCode || null,
        message: this.form.message || null,
        extranetKeyId: this.extranetKeyId,
      };

      // APIを呼び出しエクストラネット接続申請を行う
      await this.$api.extranetApi.postExtranet(reqData);
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** 検索機能付きプルダウンで選択した値から表示値のみ返す */
    getCustomLabel({ vpnVnCode, vnName, vnType }: never) {
      return `${vnName}:${vpnVnCode}(${vnType})`;
    },
  },
});
</script>
